import { Image, Box, Text } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { languageMapping } from "../../../i18n/config";
import i18next from "i18next";
import belowIcon from "../images/below.png";
import newsContainer from "../images/news.png";
import { useTranslation } from "react-i18next";

// 將公告列表中的每個公告根據當前語言進行處理，選擇合適的語言版本
const getList = (announcementList) => {
  if (!announcementList || announcementList.length === 0) return [];

  const processedArrayData = announcementList.map((item) => ({
    ...item,
    currentLang:
      item.news_language.find(
        (langItem) => langItem === languageMapping(i18next.language),
      ) || item.news_language?.[0],
  }));

  return processedArrayData;
};

/**
 * @description 公告內容組件，根據傳入的公告列表顯示相關信息
 * @param {Array} announcementList - 包含公告信息的列表
 * @return {JSX.Element}
 */
const AnnouncementContent = ({ reduxAnnouncementList }) => {
  const { t } = useTranslation();
  // 本地狀態管理公告列表、展開的公告 ID 以及內容溢出的公告 ID 集合
  const [announcementList, setAnnouncementList] = useState([]);
  // 用於存儲公告內容的 ref，以便檢測內容是否溢出
  const [expendedId, setExpendedId] = useState(null);
  // 用於存儲內容溢出的公告 ID，使用 Set 以避免重複
  const [overflowIds, setOverflowIds] = useState(new Set());
  // 使用 useRef 來存儲每個公告內容的 DOM 元素，以便後續檢測內容是否溢出
  const contentRefs = useRef({});

  // 切換公告內容的展開與收起狀態
  const toggleExpand = (id) => {
    setExpendedId((prev) => (prev === id ? null : id));
  };

  // 當 redux 中的公告列表發生變化時，根據當前語言處理公告列表並更新本地狀態
  useEffect(() => {
    const list = getList(reduxAnnouncementList.list);
    setAnnouncementList(list);
  }, [reduxAnnouncementList]);

  // 當公告列表或展開的公告 ID 發生變化時，檢測每個公告內容是否溢出，並更新 overflowIds 狀態
  useEffect(() => {
    const newOverflowIds = new Set();
    announcementList.forEach((announcement) => {
      const uid = announcement.currentLang.uid;
      const element = contentRefs.current[uid];
      if (element && element.scrollHeight > element.clientHeight) {
        newOverflowIds.add(uid);
      }
    });
    setOverflowIds(newOverflowIds);
  }, [announcementList]);

  if (!announcementList || announcementList.length === 0) {
    return (
      <Box className="w-full text-center p-4">
        <Text>{t("announcement.no_announcement")}</Text>
      </Box>
    );
  }

  return (
    <Box
      className="space-y-4"
      paddingBottom={"6em"}
      overflow={"scroll"}
      height={"100%"}
    >
      {announcementList?.map((data) => {
        const currentAnnouncement = data?.currentLang;
        const isExpended = expendedId === currentAnnouncement.uid;
        const hasOverflow = overflowIds.has(currentAnnouncement.uid);
        return (
          <Box
            key={currentAnnouncement.uid}
            bgImage={newsContainer}
            bgSize="100% 100%"
            height={"fit-content"}
            padding={"1em 1em"}
            className="space-y-2"
          >
            <Text
              fontWeight="bold"
              fontSize="1.2em"
              color={"#D5C871"}
              sx={{ filter: "drop-shadow(1px 2px 3px #A50000)" }}
            >
              {currentAnnouncement.title}
            </Text>
            <Box display={"flex"} alignItems={"center"}>
              <Box
                ref={(element) =>
                  (contentRefs.current[currentAnnouncement.uid] = element)
                }
                color={"#986A2E"}
                {...(!isExpended && {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  sx: {
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                  },
                })}
              >
                {currentAnnouncement.content}
              </Box>
              {hasOverflow && !isExpended && (
                <Box
                  cursor={"pointer"}
                  display={"flex"}
                  justifyContent={"center"}
                  minWidth={"2em"}
                  maxHeight={"2em"}
                  _hover={{
                    transform: "scale(1.1)",
                    transition: "transform 0.2s",
                  }}
                  onClick={() => toggleExpand(currentAnnouncement.uid)}
                >
                  <Image src={belowIcon} transition="transform 0.3s ease" />
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default AnnouncementContent;
